/**
 * Phase 120 (Plan 120-04, D-120-23) — publish/subscribe channel between the task
 * state API clients and the global ReleaseFlagDeferralNotice.
 *
 * When the backend answers a Done move with `deferred` (the ticket's release
 * flag is off in production until verified) the task did NOT move. The two API
 * clients that change task state publish here; the notice mounted in Layout
 * subscribes. A module-level listener set keeps the clients free of React.
 */

/** Mirrors the backend `ReleaseFlagDeferral` response field. */
export interface ReleaseFlagDeferral {
  reason: 'release_flag_unverified';
  ticket: string;
  flagKeys: string[];
  /** Phase 129 (D-16) — the project the ticket belongs to; absent on older payloads. */
  projectId?: number;
}

/**
 * MKS-312 (Plan 126-05 / D-19) — a Done move held because the Factory™ change spans several
 * repositories and not every one is deployed yet. `pendingRepositories` names the repositories
 * still outstanding; absent when the server did not list them.
 */
export interface FactoryFamilyDeferral {
  reason: 'factory_family_incomplete';
  ticket: string;
  flagKeys: string[];
  pendingRepositories?: string[];
}

/** Any reason a Done move can be deferred with. */
export type DoneDeferral = ReleaseFlagDeferral | FactoryFamilyDeferral;

type Listener = (deferral: DoneDeferral) => void;

const listeners = new Set<Listener>();

/** Notify every subscriber. */
export function publishReleaseFlagDeferral(deferral: DoneDeferral): void {
  for (const listener of [...listeners]) listener(deferral);
}

/** Subscribe to deferrals; returns the unsubscribe function. */
export function subscribeReleaseFlagDeferral(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
